import { useQueries, useQuery } from '@tanstack/react-query';
import { Badge, Descriptions, Divider, Flex, Grid, Spin, Tabs, type TabsProps, Typography } from 'antd';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getTicket } from 'src/apis/helpdesk/ticket.api';
import { BtnHandleList } from 'src/components/v2/Button';
import { AppContext } from 'src/contexts/app.context';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { ApprovalStatus, TicketStatus } from 'src/types/helpdesk/ticket.type';

import { getRefAdvancePaymentTicketsForTicket } from 'src/apis/hr_ticket/advancePaymentTicket.api';
import { getRefBusinessTripTicketsForTicket } from 'src/apis/hr_ticket/businessTripTicket.api';
import { getRefLeaveTicketsForTicket } from 'src/apis/hr_ticket/leaveTicket.api';
import { getRefOvertimeTicketsForTicket } from 'src/apis/hr_ticket/overtimeTicket.api';
import { getRefPurchaseRequestTicketsForTicket } from 'src/apis/hr_ticket/purchaseRequestTicket.api';
import { getRefTransportationTicketsForTicket } from 'src/apis/hr_ticket/transportationTicket.api';
import { TicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { HrCategoryEn } from 'src/constants/hrCategory.enum';
import { TicketContext } from 'src/contexts/ticket.context';
import './TicketDetail.scss';
import TicketDetailAssign from './TicketDetailAssign';
import TicketDetailInformationCollection from './TicketDetailInformationCollection';
import TicketDetailSolution from './TicketDetailSolution';
import {
  AdvancePaymentTicket,
  BusinessTripTicket,
  LeaveTicket,
  OvertimeTicket,
  PurchaseRequestTicket,
  TransportationTicket
} from './TicketRelative';
const { Title, Text } = Typography;

export interface OpenType {
  isOpen: boolean;
  isManpowerSupplyForm?: boolean;
  ticketId: string;
}

export default function TicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { setTicket, setLoading } = useContext(TicketDetailContext);
  const [isHasRelatedTicket, setIsHasRelatedTicket] = useState<boolean>(true);
  const { ticketSelected } = useContext(AppContext);

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const { id } = useParams();

  const { data, isFetching } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => getTicket(String(id)),
    enabled: !!id && !!ticketSelected
  });

  const [
    refLeaveTicketsForTicketQuery,
    refBusinessTripTicketsForTicketQuery,
    refOvertimeTicketsForTicketQuery,
    refTransportationTicketsForTicketQuery,
    refAdvancePaymentTicketsForTicketQuery,
    refPurchaseRequestTicketsForTicketQuery
  ] = useQueries({
    queries: [
      {
        queryKey: ['refLeaveTicketsForTicket', data?.data?.id],
        queryFn: () => getRefLeaveTicketsForTicket(String(data?.data?.id)),
        staleTime: 60 * 1000,
        enabled:
          !!data?.data?.id &&
          !!data.data.ticketCategory.ticketCategoryMappings.some(
            (x) => x.hrTicketCategory.code === HrCategoryEn.LV_TICKET
          )
      },
      {
        queryKey: ['refBusinessTripTicketsForTicket', data?.data?.id],
        queryFn: () => getRefBusinessTripTicketsForTicket(String(data?.data?.id)),
        staleTime: 60 * 1000,
        enabled:
          !!data?.data?.id &&
          (!!data.data.ticketCategory.ticketCategoryMappings.some(
            (x) => x.hrTicketCategory.code === HrCategoryEn.BT_TICKET
          ) ||
            data.data.manpowerSupplyForms.length > 0)
      },
      {
        queryKey: ['refOvertimeTicketsForTicket', data?.data?.id],
        queryFn: () => getRefOvertimeTicketsForTicket(String(data?.data?.id)),
        staleTime: 60 * 1000,
        enabled:
          !!data?.data?.id &&
          (!!data.data.ticketCategory.ticketCategoryMappings.some(
            (x) => x.hrTicketCategory.code === HrCategoryEn.OT_TICKET
          ) ||
            data.data.manpowerSupplyForms.length > 0)
      },
      {
        queryKey: ['refTransportationTicketsForTicket', data?.data?.id],
        queryFn: () => getRefTransportationTicketsForTicket(String(data?.data?.id)),
        staleTime: 60 * 1000,
        enabled:
          !!data?.data?.id &&
          !!data.data.ticketCategory.ticketCategoryMappings.some(
            (x) => x.hrTicketCategory.code === HrCategoryEn.TRANS_TICKET
          )
      },
      {
        queryKey: ['refAdvancePaymentTicketsForTicket', data?.data?.id],
        queryFn: () => getRefAdvancePaymentTicketsForTicket(String(data?.data?.id)),
        staleTime: 60 * 1000,
        enabled:
          !!data?.data?.id &&
          !!data.data.ticketCategory.ticketCategoryMappings.some(
            (x) => x.hrTicketCategory.code === HrCategoryEn.AP_TICKET
          )
      },
      {
        queryKey: ['refPurchaseRequestTicketsForTicket', data?.data?.id],
        queryFn: () => getRefPurchaseRequestTicketsForTicket(String(data?.data?.id)),
        staleTime: 60 * 1000,
        enabled:
          !!data?.data?.id &&
          !!data.data.ticketCategory.ticketCategoryMappings.some(
            (x) => x.hrTicketCategory.code === HrCategoryEn.PR_TICKET
          )
      }
    ]
  });

  function checkCancelTicket(data: any): boolean {
    return data?.filter((x: any) => x?.ticketStatusId !== TicketStatus.Canceled).length > 0;
  }

  useEffect(() => {
    if (
      checkCancelTicket(refLeaveTicketsForTicketQuery.data?.data) ||
      checkCancelTicket(refBusinessTripTicketsForTicketQuery.data?.data) ||
      checkCancelTicket(refOvertimeTicketsForTicketQuery.data?.data) ||
      checkCancelTicket(refTransportationTicketsForTicketQuery.data?.data) ||
      checkCancelTicket(refAdvancePaymentTicketsForTicketQuery.data?.data) ||
      checkCancelTicket(refPurchaseRequestTicketsForTicketQuery.data?.data)
    ) {
      setIsHasRelatedTicket(true);
    } else {
      setIsHasRelatedTicket(false);
    }
  }, [
    refLeaveTicketsForTicketQuery.data,
    refBusinessTripTicketsForTicketQuery.data,
    refOvertimeTicketsForTicketQuery.data,
    refTransportationTicketsForTicketQuery.data,
    refAdvancePaymentTicketsForTicketQuery.data,
    refPurchaseRequestTicketsForTicketQuery.data,
    data?.data.manpowerSupplyForms.length
  ]);

  const itemsTab: TabsProps['items'] = [
    {
      key: '1',
      label: <Text strong>Thu thập thông tin</Text>,
      children: <TicketDetailInformationCollection />
    },
    {
      key: '2',
      label: (
        <Text strong disabled={!(data?.data?.ticketSolutions && data?.data?.ticketSolutions?.length > 0)}>
          Danh sách giải pháp
        </Text>
      ),
      children: <TicketDetailSolution />,
      disabled: !(data?.data?.ticketSolutions && data?.data?.ticketSolutions?.length > 0)
    },
    {
      key: '3',
      label: (
        <Text strong disabled={!(data?.data?.assignLogs && data?.data?.assignLogs?.length > 0)}>
          Danh sách phân công
        </Text>
      ),
      children: <TicketDetailAssign />,
      disabled: !(data?.data?.assignLogs && data?.data?.assignLogs?.length > 0)
    },
    {
      key: '4',
      label: (
        <Flex gap={6} align='center'>
          <Text strong>Phiếu liên quan</Text>
          {isHasRelatedTicket && <Badge status='processing' color='green' />}
        </Flex>
      ),
      children: (
        <Flex gap={10} vertical>
          {Number(refLeaveTicketsForTicketQuery?.data?.data?.length) > 0 && (
            <LeaveTicket {...refLeaveTicketsForTicketQuery} />
          )}
          {Number(refTransportationTicketsForTicketQuery?.data?.data?.length) > 0 && (
            <TransportationTicket {...refTransportationTicketsForTicketQuery} />
          )}
          {Number(refAdvancePaymentTicketsForTicketQuery.data?.data.length) > 0 && (
            <AdvancePaymentTicket {...refAdvancePaymentTicketsForTicketQuery} />
          )}
          {Number(refBusinessTripTicketsForTicketQuery.data?.data.length) > 0 && (
            <BusinessTripTicket {...refBusinessTripTicketsForTicketQuery} />
          )}
          {Number(refOvertimeTicketsForTicketQuery.data?.data.length) > 0 && (
            <OvertimeTicket {...refOvertimeTicketsForTicketQuery} />
          )}
          {Number(refPurchaseRequestTicketsForTicketQuery.data?.data.length) > 0 && (
            <PurchaseRequestTicket {...refPurchaseRequestTicketsForTicketQuery} />
          )}
        </Flex>
      )
    }
  ];

  useEffect(() => {
    if (data) {
      setTicket(data);
      setLoading(isFetching);
      setInvalidKey(JSON.stringify(data?.data));
    }
  }, [data, isFetching, setTicket, setLoading, setInvalidKey]);

  return (
    <div className='ticket-detail-wrapper'>
      <BtnHandleList isHasRelatedTicket={isHasRelatedTicket} />
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <Flex justify='center'>
        <Title level={5} className='ticket-detail-title'>
          Thông tin ticket
        </Title>
      </Flex>

      <Fragment>
        <Spin size='small' spinning={isFetching}>
          <Descriptions
            layout='horizontal'
            labelStyle={{ width: '135px' }}
            column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
          >
            <Descriptions.Item label='Mã ticket'>
              <Text strong>{data?.data?.ticketCode}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Trạng thái'>
              <TicketStatusTag ticketStatus={data?.data?.ticketStatus} />
            </Descriptions.Item>
            <Descriptions.Item label='Danh mục' span={colspan}>
              <Text strong italic>
                {data?.data?.ticketCategory?.name ?? ''}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label='Người tạo'>
              <Text>{data?.data?.createdByObj?.name ?? ''}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='BP Tiếp nhận'>
              <Text>{data?.data?.departmentHandle ?? ''}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Người xử lý' span={colspan}>
              <Text>{data?.data?.handlerObj?.name ?? ''}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Tên giải pháp' span={colspan}>
              <Text> {data?.data?.ticketSolutions?.find((x) => x.isApproval)?.title || ''}</Text>
            </Descriptions.Item>
            {data?.data?.ticketStatus?.id === TicketStatus.Reject ? (
              data?.data.approvalLogs.some((item) => item.approvalStatus === ApprovalStatus.Reject) ? (
                <Descriptions.Item label='Lý do từ chối phê duyệt' span={colspan}>
                  <Flex gap={5} vertical>
                    <Text strong>{data?.data?.reason.name ?? ''}</Text>
                    <Text italic>{data?.data?.approvalLogs.reverse()?.[0].noteReject ?? ''}</Text>
                  </Flex>
                </Descriptions.Item>
              ) : (
                <Descriptions.Item label='Lý do từ chối tiếp nhận' span={colspan}>
                  <Flex gap={5} vertical>
                    <Text strong>{data?.data?.reason.name ?? ''}</Text>
                    <Text italic>{data?.data?.noteReject ?? ''}</Text>
                  </Flex>
                </Descriptions.Item>
              )
            ) : (
              <></>
            )}
            {data?.data?.ticketStatus?.id === TicketStatus.HandleCompleted ||
              (data?.data?.ticketStatus?.id === TicketStatus.Finished && (
                <Descriptions.Item label='Ghi chú hoàn thành' span={colspan}>
                  <Text> {data?.data?.noteComplete ?? ''}</Text>
                </Descriptions.Item>
              ))}
          </Descriptions>

          <Tabs defaultActiveKey='1' items={itemsTab} className='ticket-detail-tab' />
        </Spin>
      </Fragment>
    </div>
  );
}
